import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";

// Single source of truth for trade-based styling across the Resource Library:
// the resource card trade badge, the featured carousel, and the filter sidebar
// all consume these maps so a trade always renders in the same color.

const DEFAULT_TRADE_BADGE_CLASS =
  "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300";

const TRADE_BADGE_CLASS: Record<string, string> = {
  "all trades": "bg-red-600 text-white",
  "multi-trade": "bg-red-600 text-white",
  "no trade": "bg-gray-900 text-white dark:bg-black",
  plumbing: "bg-blue-600 text-white",
  electrical: "bg-amber-500 text-white",
  hvac: "bg-teal-600 text-white",
  roofing: "bg-green-600 text-white",
};

// The same per-trade palette as solid text, for the role line, title, and
// View link. Keyed identically to TRADE_BADGE_CLASS.
const TRADE_TEXT_CLASS: Record<string, string> = {
  "all trades": "text-red-700 dark:text-red-400",
  "multi-trade": "text-red-700 dark:text-red-400",
  "no trade": "text-gray-900 dark:text-white",
  plumbing: "text-blue-600 dark:text-blue-400",
  electrical: "text-amber-600 dark:text-amber-400",
  hvac: "text-teal-700 dark:text-teal-400",
  roofing: "text-green-700 dark:text-green-400",
};
const DEFAULT_TRADE_TEXT_CLASS = "text-gray-900 dark:text-white";

// Solid badge color for a trade by name (same palette as the resource card's
// trade badge). Used by the filter sidebar to color a selected trade's count
// pill. Falls back to the neutral badge color for trades without a dedicated one.
export function getTradeBadgeClassByName(tradeName: string): string {
  return (
    TRADE_BADGE_CLASS[tradeName.toLowerCase()] ?? DEFAULT_TRADE_BADGE_CLASS
  );
}

// Resolves a resource's trade to a display key + label. All-trades and
// multi-trade collapse to their own keys; a single trade uses its name; a
// resource with no trade gets the "no trade" badge.
function resolveTradeBadge(resource: GetResourceSearchResultsItem): {
  key: string;
  label: string;
} {
  const trades = resource.trades ?? [];

  if (resource.isAllTrades) {
    return { key: "all trades", label: "All Trades" };
  }

  if (resource.isMultiTrade) {
    return { key: "multi-trade", label: "Multi-Trade" };
  }

  const name = trades[0]?.name;
  if (!name) {
    return { key: "no trade", label: "No Trade" };
  }

  return { key: name.toLowerCase(), label: name };
}

// Resolves a resource's trade into its badge descriptor plus the badge
// background and accent-text color classes. Shared by the resource card and the
// featured carousel, so both render trades identically.
export function getResourceTradeStyling(
  resource: GetResourceSearchResultsItem,
): {
  tradeBadge: { key: string; label: string };
  tradeBadgeClass: string;
  tradeColorClass: string;
} {
  const tradeBadge = resolveTradeBadge(resource);
  const tradeBadgeClass =
    TRADE_BADGE_CLASS[tradeBadge.key] ?? DEFAULT_TRADE_BADGE_CLASS;
  const tradeColorClass =
    TRADE_TEXT_CLASS[tradeBadge.key] ?? DEFAULT_TRADE_TEXT_CLASS;

  return { tradeBadge, tradeBadgeClass, tradeColorClass };
}
